/**
 * RFC 2646 / RFC 3676 `format=flowed`
 * decoder.
 *
 * "Flowed" plain-text bodies use trailing-space soft-line-breaks so
 * the receiver can re-wrap the paragraphs to its own window width.
 * Without unfolding, a flowed body shows up in the inspector as if
 * every soft line break were hard, which makes paragraphs look
 * fragmented and breaks search-in-body for any phrase that spans a
 * soft wrap.
 *
 * The decoder is conservative — it only touches lines whose trailing
 * whitespace + line-ending shape matches the spec, and it always
 * preserves:
 *
 *  - The signature delimiter `-- ` (RFC 3676 §4.3): even though the
 *  line ends with a space, it must NOT be joined with the next
 *  line. The signature line is the only "stuffed" line where
 *  the trailing space is structural.
 *  - Quote depth: `>` lines fold separately from non-quoted lines
 *  (RFC 3676 §4.5).
 *  - Empty lines: paragraph boundaries are hard and survive.
 *
 * When `delsp=yes` is set (RFC 3676 §4.1), the trailing space that
 * marks the soft line break is removed before joining; otherwise the
 * space is preserved (it becomes the inter-word separator).
 */

export interface FormatFlowedOptions {
  /** RFC 3676 §4.1 — DEL-SP=YES strips the soft-break space at join time. */
  delsp?: boolean;
}

/**
 * Unfold a `format=flowed` body. Pure / synchronous; safe to call on
 * any string — non-flowed input passes through with line endings
 * normalised to `\n`.
 */
export function unfoldFormatFlowed(
  body: string,
  options: FormatFlowedOptions = {},
): string {
  const delsp = options.delsp === true;
  // Normalise to LF first so the join logic doesn't have to deal with
  // mixed line endings on the wire. Trim trailing newlines so the
  // split doesn't yield a phantom trailing "" — wire form typically
  // ends with a final CRLF that's structural (not part of any line).
  const lines = body.replace(/\r\n/g, "\n").replace(/\n+$/, "").split("\n");

  // Single-pass scan. We accumulate "flowed-into-current" text in a
  // mutable string and push completed paragraphs onto `out` when we
  // hit a hard line break (line without a trailing space, signature
  // delimiter, or a quote-depth transition).
  const out: string[] = [];
  let buffer = "";
  let currentQuote = -1;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? "";
    const depth = leadingQuoteDepth(raw);
    const stripped = raw.slice(depth);
    // Space-stuffing reversal: a line beginning with a space had it
    // added by the sender to escape false `From ` lines or `>` quotes.
    const dequoted = stripped.startsWith(" ") ? stripped.slice(1) : stripped;
    const trailingSpace = dequoted.length > 0 && dequoted.endsWith(" ");
    // RFC 3676 §4.3 — the signature separator is the literal `-- `.
    // Treat it as a hard line even though it carries a trailing space.
    const isSignature = dequoted === "-- ";
    const soft = trailingSpace && !isSignature;

    // A change in quote depth flushes the in-flight buffer — quoted
    // and unquoted runs never flow together.
    if (currentQuote !== depth && buffer.length > 0) {
      out.push(reflowEmit(buffer, currentQuote));
      buffer = "";
    }
    currentQuote = depth;

    if (soft) {
      // Trim the trailing space when delsp=yes; otherwise keep it as
      // the inter-word separator that RFC 2646 §4.2 §a3 mandates.
      buffer += delsp ? dequoted.slice(0, -1) : dequoted;
    } else {
      // Hard line — flush buffer + this line, then reset.
      buffer += dequoted;
      out.push(reflowEmit(buffer, depth));
      buffer = "";
    }
  }
  // The wire form often ends without a trailing newline; flush
  // whatever sat in the buffer so we don't drop content.
  if (buffer.length > 0) {
    out.push(reflowEmit(buffer, currentQuote));
  }
  return out.join("\n");
}

/**
 * Count the leading `>` characters on a line. `>>` returns 2, etc.
 * Per RFC 3676 §4.5 quote markers are bare `>` with no intervening
 * space; tools that emit `> >` for nested quotes are non-conformant
 * and we treat them as depth=1 (which matches what most MUAs render).
 */
function leadingQuoteDepth(line: string): number {
  let d = 0;
  while (d < line.length && line[d] === ">") d++;
  return d;
}

function reflowEmit(text: string, depth: number): string {
  if (depth === 0) return text;
  return ">".repeat(depth) + text;
}

/**
 * Parse the content-type parameter blob to decide whether the body
 * should be unfolded at all. Returns `{ flowed, delsp }` so the
 * caller can pass `delsp` through to {@link unfoldFormatFlowed}.
 */
export function detectFormatFlowed(contentType: string): {
  flowed: boolean;
  delsp: boolean;
} {
  let flowed = false;
  let delsp = false;
  for (const segment of contentType.split(";")) {
    const trimmed = segment.trim().toLowerCase();
    if (trimmed.startsWith("format=")) {
      const value = trimmed.slice("format=".length).replace(/^"|"$/g, "");
      flowed = value === "flowed";
    }
    if (trimmed.startsWith("delsp=")) {
      const value = trimmed.slice("delsp=".length).replace(/^"|"$/g, "");
      delsp = value === "yes";
    }
  }
  return { flowed, delsp };
}
